import React, {useEffect, useState} from 'react';
import {Box, Dialog, IconButton, Stack, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FileTypeIcon from './FileTypeIcon';
import SpreadsheetPreview, {isSpreadsheet} from './SpreadsheetPreview';

// Maria, 17 October 2026: documents open in a preview pop-up, with 'Download' in the pop-up rather than in the list.
// PDFs and pictures are shown; other files (Word, Excel, PowerPoint) cannot be shown in a browser, so the pop-up
// says so and offers 'Download'.

const SUPPORTED_MIME_TYPES = {
    'application/pdf': 'pdf',
    'image/*': 'image',
};

const resolveMimeType = (type = '') => {
    if (SUPPORTED_MIME_TYPES[type]) {
        return SUPPORTED_MIME_TYPES[type];
    }

    const [mainType] = type.split('/');

    return SUPPORTED_MIME_TYPES[`${mainType}/*`] ?? null;
};

// AD-2725 (23 October 2026): a file can come back from storage marked as plain text or with no type, so a PDF or
// picture is also recognised from the end of its name, and given its proper type so the browser can show it
const TYPES_BY_EXTENSION = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp'
};

export const previewMimeType = (type, name = '') => {
    if (resolveMimeType(type ?? '')) {
        return type;
    }
    const extension = (name.split('.').pop() || '').toLowerCase();
    return TYPES_BY_EXTENSION[extension] ?? null;
};

export const CANNOT_SHOW = 'This file cannot be shown here. Click on \'Download\' to open it.';

// Maria, 17 October 2026: 'Download' lets the person choose where to save the file. Chrome and Edge on a computer
// open the 'Save As' window; other browsers save where they are set to save downloads.
export const downloadBlob = async (blob, name) => {
    if (typeof window.showSaveFilePicker === 'function') {
        try {
            const handle = await window.showSaveFilePicker({suggestedName: name || 'document'});
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();
            return;
        } catch (error) {
            // Closing the 'Save As' window is not a fault: nothing is saved.
            if (error?.name === 'AbortError') return;
        }
    }

    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.setAttribute('download', name || 'document');
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/* eslint-disable react/prop-types */

// Prints a PDF or image through a hidden frame, so the person stays on the page
const printAsset = (url) => {
    const frame = window.document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.src = url;
    frame.onload = () => {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        setTimeout(() => frame.remove(), 60000);
    };
    window.document.body.appendChild(frame);
};

const DocumentPreviewDialog = ({
                                   open,
                                   onClose,
                                   document,
                                   documentName,
                                   isLoading,
                                   maxWidth = 'lg',
                                   // A smaller pop-up with the page still visible behind it (Maria, 18 October 2026: consent policies)
                                   overPage = false
                               }) => {
    const [assetUrl, setAssetUrl] = useState('');
    const [assetType, setAssetType] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!open || isLoading || !(document instanceof Blob)) {
            return undefined;
        }

        // a spreadsheet is drawn as a table; PDFs and pictures are shown as they are
        const mimeType = previewMimeType(document.type, documentName);
        const type = isSpreadsheet(document.type, documentName) ? 'spreadsheet' : resolveMimeType(mimeType ?? '');
        const shown = mimeType && mimeType !== document.type ? new Blob([document], {type: mimeType}) : document;
        const url = type && type !== 'spreadsheet' ? URL.createObjectURL(shown) : '';
        setAssetType(type);
        setAssetUrl(url);
        setFailed(false);

        return () => {
            if (url) URL.revokeObjectURL(url);
            setAssetUrl('');
            setAssetType(null);
        };
    }, [document, open, isLoading]);

    const canDownload = !isLoading && document instanceof Blob;

    const renderContent = () => {
        if (isLoading) {
            return <CircularProgress size={36}/>;
        }

        if (!(document instanceof Blob) || !assetType || failed) {
            return (
                <Stack alignItems="center" gap={2} sx={{p: 4, textAlign: 'center'}} data-testid="document-preview-cannot-show">
                    <FileTypeIcon name={documentName} sx={{fontSize: 64}}/>
                    <Typography variant="body1">{document instanceof Blob ? CANNOT_SHOW : 'This document could not be opened.'}</Typography>
                </Stack>
            );
        }

        if (assetType === 'spreadsheet') {
            return <SpreadsheetPreview blob={document} name={documentName} onCannotShow={() => setFailed(true)}/>;
        }

        if (assetType === 'pdf') {
            return (
                <iframe
                    src={`${assetUrl}#toolbar=0&navpanes=0&view=FitH`}
                    title={documentName || 'Document preview'}
                    width="100%"
                    height="100%"
                    style={{border: 'none', backgroundColor: '#fff'}}
                />
            );
        }

        return (
            <Box
                component="img"
                src={assetUrl}
                alt={documentName || 'Document preview'}
                sx={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: 2, backgroundColor: '#fff'}}
                onError={() => setFailed(true)}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth
            data-testid="document-preview"
            PaperProps={{sx: {borderRadius: 3, height: overPage ? 'min(70vh, 720px)' : 'calc(100% - 64px)', overflow: 'hidden', boxShadow: overPage ? 12 : undefined}}}
            slotProps={overPage ? {backdrop: {sx: {backgroundColor: 'rgba(15, 23, 42, 0.15)'}}} : undefined}
        >
            <Stack direction="row" alignItems="center" gap={1.5} sx={{px: 3, py: 1.5, borderBottom: '1px solid #eef0f3'}}>
                <FileTypeIcon name={documentName}/>
                <Typography
                    sx={{flex: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                    data-testid="document-preview-name"
                >
                    {documentName || ''}
                </Typography>
                {/* Maria, 18 October 2026: 'Download' and 'Print', as on the form responses viewer */}
                <Button
                    size="small"
                    variant="text"
                    disabled={!canDownload}
                    onClick={() => downloadBlob(document, documentName)}
                    data-testid="document-preview-download"
                >
                    Download
                </Button>
                <Button
                    size="small"
                    variant="text"
                    disabled={!canDownload || !assetUrl || !['pdf', 'image'].includes(assetType)}
                    onClick={() => printAsset(assetUrl)}
                    data-testid="document-preview-print"
                >
                    Print
                </Button>
                <IconButton onClick={onClose} aria-label="Close">
                    <CloseIcon/>
                </IconButton>
            </Stack>
            <Box sx={{flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f4f8', p: ['pdf', 'spreadsheet'].includes(assetType) ? 0 : 3}}>
                {renderContent()}
            </Box>
        </Dialog>
    );
};

export default DocumentPreviewDialog;
